import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { FlaskConical } from 'lucide-react';

interface ABTestNameModalProps {
  open: boolean;
  onConfirm: (name: string, description: string) => void;
  onCancel: () => void;
}

export function ABTestNameModal({ open, onConfirm, onCancel }: ABTestNameModalProps) {
  useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!name.trim()) {
      setError(translateInterfaceText("Informe um nome para o teste."));
      return;
    }
    setIsLoading(true);
    onConfirm(name.trim(), description.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim() && !isLoading) {
      handleConfirm();
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) setError('');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[460px] p-8">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#1D4FFF]/10 flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-[#1D4FFF]" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">{translateInterfaceText("Criar Teste A/B")}</DialogTitle>
              <DialogDescription className="text-sm mt-1 text-muted-foreground">
                {translateInterfaceText("Compare diferentes variações do seu vídeo e descubra qual converte melhor.")}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 pb-8">
          <div className="space-y-2">
            <Label htmlFor="test-name" className="text-sm font-medium">
              {translateInterfaceText("Nome do teste")}{" "}<span className="text-destructive">*</span>
            </Label>
            <Input
              id="test-name"
              placeholder={translateInterfaceText("Ex: Teste de VSLs – Produto X")}
              value={name}
              onChange={handleNameChange}
              onKeyPress={handleKeyPress}
              className="h-11"
              autoFocus
            />
            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Use um nome que ajude você a identificar facilmente este experimento no futuro.")}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-6 border-t">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button
            onClick={handleConfirm}
            disabled={!name.trim() || isLoading}
            className="min-w-[120px]"
          >
            {isLoading ? translateInterfaceText("Criando teste...") : translateInterfaceText("Criar Teste")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
